import type { Order } from '@/types';
import { orderService } from '@/services/order';

interface PickupTimeDisplayProps {
  order: Order;
  className?: string;
  showDescription?: boolean;
}

export function PickupTimeDisplay({ 
  order, 
  className = '', 
  showDescription = false 
}: PickupTimeDisplayProps) {
  
  const formatPickupTime = () => {
    // READY 상태가 아니면 기본 표시
    if (order.status !== 'READY' || !order.pickup_time) {
      switch (order.order_type) {
        case 'DEADLINE_DISCOUNT':
          return '마감할인 수령시간 내';
        case 'RESERVATION_DISCOUNT':
          return '예약한 시간에 수령';
        case 'REGULAR_ORDER':
          return order.status === 'PENDING' 
            ? '접수 후 즉시 수령 가능' 
            : '준비 완료 후 2시간 내 수령';
        default:
          return '수령 시간 확인 필요';
      }
    }

    // READY 상태이고 pickup_time이 있는 경우 상세 시간 표시
    const pickupStart = new Date(order.pickup_time);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const pickupDate = new Date(pickupStart);
    pickupDate.setHours(0, 0, 0, 0);
    
    const isToday = pickupDate.getTime() === today.getTime();
    const isTomorrow = pickupDate.getTime() === today.getTime() + (24 * 60 * 60 * 1000);
    
    let datePrefix = '';
    if (isToday) {
      datePrefix = '오늘 ';
    } else if (isTomorrow) {
      datePrefix = '내일 ';
    } else {
      datePrefix = pickupStart.toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric'
      }) + ' ';
    }
    
    const pickupStartStr = pickupStart.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });

    // orderService의 getPickupTimeDescription 사용
    const description = orderService.getPickupTimeDescription(
      order.order_type,
      order.status,
      order.pickup_time,
      (order as any).ready_at
    );
    
    if (showDescription) {
      return description;
    }
    
    // 간단한 표시 (날짜 + 시작시간)
    return `${datePrefix}${pickupStartStr}부터`;
  };

  const getStatusColor = () => {
    if (order.status === 'READY') {
      if (order.pickup_time) {
        const now = new Date();
        const pickupTime = new Date(order.pickup_time);
        if (now >= pickupTime) {
          return 'text-green-600'; // 수령 가능 시간
        }
      }
      return 'text-blue-600'; // 준비 완료, 수령 대기
    }
    
    switch (order.status) {
      case 'PENDING':
        return 'text-gray-600';
      case 'ACCEPTED':
        return 'text-orange-600';
      case 'COMPLETED':
        return 'text-green-600';
      case 'NOSHOW':
        return 'text-red-600';
      case 'CANCELLED':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = () => {
    if (order.status === 'READY') {
      if (order.pickup_time) {
        const now = new Date();
        const pickupTime = new Date(order.pickup_time);
        if (now >= pickupTime) {
          return '🟢'; // 수령 가능
        }
      }
      return '🔵'; // 준비 완료
    }
    
    switch (order.status) {
      case 'PENDING':
        return '⏳';
      case 'ACCEPTED':
        return '👨‍🍳';
      case 'COMPLETED':
        return '✅';
      case 'NOSHOW':
        return '❌';
      case 'CANCELLED':
        return '🚫';
      default:
        return '❓';
    }
  };

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <span className="text-sm">{getStatusIcon()}</span>
      <span className={`font-medium ${getStatusColor()}`}>
        {formatPickupTime()}
      </span>
    </div>
  );
}